import {AbstractFetcher} from '@/common/services/fetcher/AbstractFetcher';

type MetaQueryType = 'alternative-names' | 'stable-tag';
interface MetaQuery<T extends MetaQueryType = MetaQueryType> {
	type: T;
	args: Record<string, any>;
}

export const getPageMeta = (fetcher: AbstractFetcher) => async ({
	locationAlternateIds,
	locationsStableId,
}: {
	locationAlternateIds?: {
		locationId: string;
	};
	locationsStableId?: {
		ids: string[];
	};
}) => {
	// Build alternate location ids getter query
	const locationAlternateIdsQuery: MetaQuery<'alternative-names'>[] = [];
	if (locationAlternateIds) {
		locationAlternateIdsQuery.push({
			type: 'alternative-names',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			args: {location_id: locationAlternateIds.locationId},
		});
	}

	// Build stable location identified (aka tag) getter query
	const locationsStableIdQuery: MetaQuery<'stable-tag'>[] = [];
	if (locationsStableId) {
		for (const id of locationsStableId.ids) {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			locationsStableIdQuery.push({type: 'stable-tag', args: {location_id: id}});
		}
	}

	// Build required queries list by input params
	const query = [...locationAlternateIdsQuery, ...locationsStableIdQuery];

	const response = await fetcher.fetch<{meta: any[]}>('/api/web/page/meta', {query});

	// Transform the result list to a map by request
	const resultMap = new Map<MetaQuery, any>(query.map((queryItem, index) => [queryItem, response.meta[index]]));

	// Extract alternate location ids and pare it as a map
	let locationAlternateIdsResult: Record<string, string> = {};
	if (locationAlternateIdsQuery.length) {
		const alternatives = resultMap.get(locationAlternateIdsQuery[0]).alternatives as any[];
		locationAlternateIdsResult = alternatives.reduce<Record<string, string>>(
			(map, item) => ({
				...map,
				[item.domain]: item.location_id,
			}),
			{} as Record<string, string>,
		);
	}

	// Extract stable location id value
	const locationsStableIdResult: string[] = [];
	for (const queryItem of locationsStableIdQuery) {
		const resultItem = resultMap.get(queryItem).tag;
		locationsStableIdResult.push(resultItem);
	}

	return {
		locationAlternateIds: locationAlternateIdsResult,
		locationsStableId: locationsStableIdResult,
	};
};
